import { Box, ButtonProps, Portal, Text, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as BlankTemplateIcon } from 'assets/icons/blank_page.svg';
import { ReactComponent as NewTabIcon } from 'assets/icons/new-tab.svg';
import { ReactComponent as AccessibilityCheckIcon } from 'assets/icons/toolbar/file/AccessibilityCheck.svg';
import { ReactComponent as AccessibleIcon } from 'assets/icons/toolbar/file/Acessible.svg';
import { ReactComponent as LanguageIcon } from 'assets/icons/toolbar/file/Language.svg';
import { ReactComponent as MyTemplatesIcon } from 'assets/icons/toolbar/file/MyTemplates.svg';
import { ReactComponent as OrderIcon } from 'assets/icons/toolbar/file/Order.svg';
import { ReactComponent as TeamTemplatesIcon } from 'assets/icons/toolbar/file/TeamTemplates.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/toolbar/file/gear.svg';
import { ReactComponent as TemplatesIcon } from 'assets/icons/widgetmenu_templates.svg';
import { TEMPLATES_REDIRECT_LINK } from 'constants/links';
import {
  ACCESSIBILITY_CHECKER_OPENED,
  ALT_TEXT_LABELS_APPLIED,
  COPY_FROM_EDITOR,
  CREATE_FROM_BLANK,
  CREATE_NEW_DESIGN,
  DOCUMENT_LANGUAGE_MODAL_OPENED,
  FILE_MENU,
  READING_ORDER_TAB_OPENED,
  SAVE_AS_COPY_FROM_EDITOR,
  VISUAL_SIMULATOR_TAB_OPENED,
} from 'constants/mixpanel';
import { PAGE_GUIDE } from 'constants/mixpanel';
import { useCreateE2Design } from 'hooks/useCreateE2Design/useCreateE2Design';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { OverLimitError, createE1Design } from 'libs/e1api/infographic';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { selectInfographTitle } from 'modules/Editor/store/infographSelector';
import { setPageNumbersMenu, setPageSizeMenu } from 'modules/Editor/store/pageControlSlice';
import { PermissionBadge, UserTypes, useUser } from 'modules/User';
import { useSaveTemplate } from 'modules/User/useSaveTemplate';
import { ColorVisualIcon } from 'modules/common/components/ColorVisualIcon';
import type { MenuType } from 'modules/common/components/Menu';
import { ToolbarMenu } from 'modules/common/components/Menu/ToolbarMenu';
import { useToast } from 'modules/common/components/Toast';
import { MouseEvent, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AccessibilityViewIndex } from 'types/accessibilityViewIndex';
import { isDevelopment, isProductionDomain, isStandalone } from 'utils/environment';

import { RootState, setActiveWidget, store, useAppDispatch, useAppSelector } from '../../store';
import { selectHasShowGrid, selectHasShowMargin, selectIsShowAltTagOnCanvas } from '../../store/editorSettingsSelector';
import {
  setAccessibilityViewIndex,
  setShowAltTagOnCanvas,
  toggleShowGrid,
  toggleShowMargin,
} from '../../store/editorSettingsSlice';
import { useFocus } from '../Focus';
import { BLANK_TEMPLATE_ID } from './FileMenu.config';
import { DebugModal } from './components/DebugModal';
import { SetLanguageModal } from './components/SetLanguageModal';

/**
 * File menu.
 */
const E1_DESIGN_BASE_LINK = '/edit';
const TOAST_ID = 'go-classic-toast';
export const FileMenu = memo(({ onClick: onClickProp }: Omit<ButtonProps, 'ref'>) => {
  const createE2Design = useCreateE2Design();
  const { t } = useTranslation('common_navbar');
  const { fileMenuRef } = useFocus();
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Internal debug component for standalone
  const { isOpen: isDebugModalOpen, onOpen: onOpenDebugModal, onClose: onCloseDebugModal } = useDisclosure();
  const { id: e1UUid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const { trackChameleon } = useChameleon();
  const { venngageE2 } = useVenngageE1Gate();

  const { isFreeUser, isPremiumUser, isEducationUser } = useUser();

  let sourceId: string | undefined = undefined;
  let sourceType: string | undefined = undefined;
  let isE2Only: boolean | undefined = false;

  const { onSaveAsMyTemplate, onSaveAsTeamTemplate } = useSaveTemplate();

  const hasShowGrid = useAppSelector(selectHasShowGrid);
  const hasShowMargin = useAppSelector(selectHasShowMargin);

  const isShowAltTagOnCanvas = useAppSelector(selectIsShowAltTagOnCanvas);

  const onShowGrid = () => {
    dispatch(toggleShowGrid());

    Mixpanel.track(PAGE_GUIDE.eventName, { is_enabled: !hasShowGrid, type: PAGE_GUIDE.type.Grid });
  };
  const onShowMargins = () => {
    dispatch(toggleShowMargin());

    Mixpanel.track(PAGE_GUIDE.eventName, { is_enabled: !hasShowMargin, type: PAGE_GUIDE.type.Margin });
  };

  const onOpenPageSizeMenu = () => {
    // Deselect widget so page toolbar options are displayed
    dispatch(setActiveWidget([]));

    // Wait for toolbar UI to update
    setTimeout(() => dispatch(setPageSizeMenu(true)), 0);
  };

  const onOpenPageNumberMenu = () => {
    // Deselect widget so page toolbar options are displayed
    dispatch(setActiveWidget([]));

    // Wait for toolbar UI to update
    setTimeout(() => dispatch(setPageNumbersMenu(true)), 0);
  };

  if (venngageE2 != null) {
    try {
      const data = JSON.parse(venngageE2.sourceData ?? '');

      if (typeof data.converted_from_e1Template === 'string') {
        sourceId = data.converted_from_e1Template;
      }

      // Keep as the reference because the propery exists.
      // if (typeof data.copied_from === 'string') {
      //   sourceId = data.copied_from;
      // }

      //dont let user go classic if the design is from e2 saved template
      if (typeof data.parent_template === 'string' && !venngageE2?.isFromE2SavedTemplate) {
        sourceId = data.parent_template;
      }

      if (venngageE2.sourceType === 'string') {
        sourceType = data.sourceType;
      }
    } catch (error) {
      if (isDevelopment) {
        console.error('The design source data is not valid JSON', error);
      }
    }

    sourceType = venngageE2.sourceType;
    isE2Only = venngageE2.isE2Only;
  }

  const onCopyDesign = async () => {
    try {
      if (!e1UUid) {
        throw new Error('E1 UUID is not defined');
      }

      const storeState = store.getState() as RootState;
      const infographTitle = selectInfographTitle(storeState);

      const createE2RequestParams = {
        sourceId: e1UUid,
        sourceType: 'e2-infographic' as const,
        mpEvent: COPY_FROM_EDITOR.eventName,

        // Design should appear at top of My Designs
        copyToSide: false,

        // Design will appear in root folder
        folder: '',

        // Don't copy modified_at fields
        fullData: false,
        copyTitle: infographTitle,
      };

      const newDesignId = await createE2Design({
        createE2RequestParams,
        loadingToastString: t('fileMenu.makeACopy.loadingToast'),
        successToastString: t('fileMenu.makeACopy.successToast', { title: infographTitle }),
        errorToastString: t('fileMenu.makeACopy.failToast'),
      });

      if (newDesignId) {
        Mixpanel.track(SAVE_AS_COPY_FROM_EDITOR.eventName, {
          title: infographTitle,
          multipage: true,
        });
        window.open(`/design/${newDesignId}`, '_blank');
      }
    } catch (error) {
      console.error(error);
      toast({
        status: 'error',
        title: t('fileMenu.makeACopy.failToast'),
      });
    }
  };

  const onCreateFromTemplate = () => {
    Mixpanel.track(CREATE_NEW_DESIGN.eventName, { from: CREATE_NEW_DESIGN.from.Template });
    window.open(TEMPLATES_REDIRECT_LINK, '_blank');
  };
  const onCreateFromBlank = async () => {
    try {
      const createE2RequestParams = {
        sourceId: BLANK_TEMPLATE_ID,
        sourceType: 'e2-template' as const,

        mpEvent: CREATE_FROM_BLANK.eventName,

        // Design will appear in root folder
        folder: '',
      };
      const newInfographId = await createE2Design({
        createE2RequestParams,
        loadingToastString: t('fileMenu.createNew.fromBlank.loadingToast'),
        successToastString: t('fileMenu.createNew.fromBlank.successToast'),
        errorToastString: t('fileMenu.createNew.fromBlank.failToast'),
      });

      if (newInfographId) {
        Mixpanel.track(CREATE_NEW_DESIGN.eventName, { from: CREATE_NEW_DESIGN.from.Blank });
        window.open(`/design/${newInfographId}`, '_blank');
      }
    } catch (error) {
      console.error(error);
      // Show failed snackbar
      toast({
        title: t('fileMenu.createNew.fromBlank.failToast'),
        status: 'error',
      });
    }
  };

  const onClickGoClassic = (e: MouseEvent<HTMLButtonElement>) => {
    if (!sourceId || isE2Only) {
      toast({
        id: TOAST_ID,
        title: t('fileMenu.goClassic.missingSourceIdLabel'),
        status: 'error',
        duration: 8000,
      });
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    toast({
      id: TOAST_ID,
      title: t('fileMenu.goClassic.loading'),
      status: 'info',
      duration: 8000,
    });

    if (!isProductionDomain) {
      console.warn(`This design is created from type ${sourceType} with id ${sourceId}, please confirm the behavior.`);
    }

    createE1Design(sourceId!)
      .then((response) => {
        toast({ title: t('fileMenu.goClassic.success') });
        trackChameleon('Load Editor1');
        trackChameleon('Create from E2 Go Classic');

        const newId = typeof response.infographic === 'string' ? response.infographic : response.infographic.data.id;
        window.open(`${E1_DESIGN_BASE_LINK}/${newId}`, '_blank');
      })
      .catch((error) => {
        if (error instanceof OverLimitError && error.name === 'OverLimitError') {
          toast({
            title: t('fileMenu.overlimit.title'),
            description: t('fileMenu.overlimit.description'),
            status: 'error',
          });
        } else {
          toast({
            title: t('fileMenu.goClassic.failure'),
            description: error.message,
            status: 'error',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    onClickProp?.(e);
  };

  // Internal debug component for standalone
  const getDebugMenu = (): {} => {
    if (isStandalone) return { debug: { label: 'Debug', onClick: onOpenDebugModal } };
    else return {};
  };

  const getSaveAsTemplateMenu = (): {} => {
    if (!isEducationUser)
      return {
        saveAsMyTemplate: {
          label: t('fileMenu.saveAsTemplate.label'),
          data: {
            myTemplate: {
              label: t('fileMenu.saveAsTemplate.myTemplate.label'),
              Icon: MyTemplatesIcon,
              onClick: onSaveAsMyTemplate,
              Badge: isFreeUser || isPremiumUser ? <PermissionBadge permission={UserTypes.Business} /> : null,
            },
            teamTemplate: {
              label: t('fileMenu.saveAsTemplate.teamTemplate.label'),
              Icon: TeamTemplatesIcon,
              onClick: onSaveAsTeamTemplate,
              Badge: isFreeUser || isPremiumUser ? <PermissionBadge permission={UserTypes.Business} /> : null,
            },
          },
        },
      };
    else return {};
  };

  let menu: MenuType = {
    createNew: {
      label: t('fileMenu.createNew.label'),
      data: {
        fromTemplate: {
          label: t('fileMenu.createNew.fromTemplate'),
          Icon: TemplatesIcon,
          onClick: onCreateFromTemplate,
        },
        fromBlank: {
          label: t('fileMenu.createNew.fromBlank.label'),
          Icon: BlankTemplateIcon,
          onClick: onCreateFromBlank,
        },
      },
    },
    // Hide below for now
    // showSmartGuides: {
    //   label: 'Show smart guides',
    //   checked: isSmartGuidesOpen,
    //   onClick: setIsSmartGuidesOpen.toggle,
    // },
    // showPrintBleed: {
    //   label: 'Show print bleed',
    //   checked: isShowPrintBleedOpen,
    //   onClick: setIsShowPrintBleedOpen.toggle,
    // },
    makeACopy: {
      label: t('fileMenu.makeACopy.label'),
      onClick: onCopyDesign,
    },
    ...getSaveAsTemplateMenu(),
    goClassic: {
      label: t('fileMenu.goClassic.label'),
      onClick: onClickGoClassic,
      rightIcon: NewTabIcon,
      tooltip: <GoClassicButtonTooltip />,
    },
    divider02: {},
    pageSettings: {
      Icon: SettingsIcon,
      label: 'Page settings',
      data: {
        pageSize: {
          label: t('fileMenu.pageSize'),
          onClick: onOpenPageSizeMenu,
        },
        pageNumber: {
          label: t('fileMenu.pageNumbers'),
          onClick: onOpenPageNumberMenu,
        },
        divider01: {},
        showGrid: {
          label: t('fileMenu.gridLabel'),
          checked: hasShowGrid,
          onClick: onShowGrid,
          closeOnSelect: false,
        },
        showMargin: {
          label: t('fileMenu.marginLabel'),
          checked: hasShowMargin,
          onClick: onShowMargins,
          closeOnSelect: false,
        },
      },
    },
    divider03: {},
    accessibility: {
      label: t('fileMenu.accessibilityLabel'),
      Icon: AccessibleIcon,
      data: {
        // takeATour: {
        //   label: 'Take a tour',
        // },
        // divider01: {},
        checkAccessibility: {
          Icon: AccessibilityCheckIcon,
          label: t('fileMenu.checkAccessibilityLabel'),
          onClick: () => {
            dispatch(setAccessibilityViewIndex(AccessibilityViewIndex.CHECK));

            Mixpanel.track(ACCESSIBILITY_CHECKER_OPENED, { from: FILE_MENU });
          },
        },
        editReadingOrder: {
          Icon: OrderIcon,
          label: t('fileMenu.readingOrderLabel'),
          onClick: () => {
            dispatch(setAccessibilityViewIndex(AccessibilityViewIndex.EDIT));

            Mixpanel.track(READING_ORDER_TAB_OPENED, { from: FILE_MENU });
          },
        },
        setDocumentLanguage: {
          Icon: LanguageIcon,
          label: t('fileMenu.documentLanguageLabel'),
          onClick: () => {
            onOpen?.();

            Mixpanel.track(DOCUMENT_LANGUAGE_MODAL_OPENED, { from: FILE_MENU });
          },
        },
        visualSimulator: {
          Icon: ColorVisualIcon,
          label: t('fileMenu.visualSimulatorLabel'),
          onClick: () => {
            dispatch(setAccessibilityViewIndex(AccessibilityViewIndex.SIMULATOR));

            Mixpanel.track(VISUAL_SIMULATOR_TAB_OPENED, { from: FILE_MENU });
          },
        },
        divider01: {},
        toggleAltTextTag: {
          label: t('fileMenu.showAltTextOnCanvas'),
          onClick: () => {
            Mixpanel.track(ALT_TEXT_LABELS_APPLIED.eventName, { is_enabled: !isShowAltTagOnCanvas });

            dispatch(setShowAltTagOnCanvas(!isShowAltTagOnCanvas));
          },
          checked: isShowAltTagOnCanvas,
          closeOnSelect: false,
        },
      },
    },
    ...getDebugMenu(),
  };

  return (
    <>
      <ToolbarMenu zIndex='navbar' name={t('fileMenu.label')} menu={menu} ref={fileMenuRef}>
        {t('fileMenu.label')}
      </ToolbarMenu>
      <Portal>
        <SetLanguageModal isOpen={isOpen} onClose={onClose} />
        {isStandalone && <DebugModal isOpen={isDebugModalOpen} onClose={onCloseDebugModal} />}
      </Portal>
    </>
  );
});
FileMenu.displayName = 'FileMenu';

const GoClassicButtonTooltip = memo(() => {
  const { t } = useTranslation('common_navbar');
  return (
    <Box textAlign='center'>
      <Text>{t('fileMenu.goClassic.tooltip-p1')}</Text>
      <Text>{t('fileMenu.goClassic.tooltip-p2')}</Text>
    </Box>
  );
});

GoClassicButtonTooltip.displayName = 'GoClassicButtonTooltip';
